import { createTimeline } from 'animejs/timeline';

export function initHomeRocket(panel) {
    const artwork = panel?.querySelector('.home-rocket');
    const trail = artwork?.querySelector('.home-rocket__trail');
    const ship = artwork?.querySelector('.home-rocket__ship');
    const surfaces = trail?.querySelectorAll('[data-trail-surface]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const desktop = window.matchMedia('(min-width: 992px)');

    if (
        !ship ||
        surfaces?.length !== 2 ||
        artwork.dataset.rocketInitialized ||
        reducedMotion.matches ||
        !desktop.matches ||
        document.hidden ||
        !('IntersectionObserver' in window)
    ) {
        return;
    }

    // Follow the centre of the original ribbon, from the lower-right tail to
    // the rocket's existing position. The first/last legs extend beyond the SVG.
    const points = [
        [1800, 1250],
        [1491, 986],
        [1366, 855],
        [1269, 735],
        [1197, 612],
        [1158, 490],
        [1157, 350],
        [1185, 233],
        [1238, 158],
        [1310, 125],
        [1383, 138],
        [1440, 182],
        [1456, 256],
        [1458, 278],
        [1434, 350],
        [1382, 437],
        [1303, 517],
        [1201, 576],
        [1079, 621],
        [944, 646],
        [804, 646],
        [659, 619],
        [516, 570],
        [388, 496],
        [277, 399],
        [180, 282],
        [105, 159],
        [47, 20],
        [(195.5 - 218) * (1533 / 582), (222.5 - 299) * (1036 / 410)]
    ];
    const ns = 'http://www.w3.org/2000/svg';
    const path = document.createElementNS(ns, 'path');
    const point = (value) => value.map((coordinate) => coordinate.toFixed(2)).join(',');
    const segments = points.slice(1).map((end, index) => {
        const start = points[index];
        const previous = points[Math.max(0, index - 1)];
        const next = points[Math.min(points.length - 1, index + 2)];
        const c1 = start.map((value, axis) => value + (end[axis] - previous[axis]) / 6);
        const c2 = end.map((value, axis) => value - (next[axis] - start[axis]) / 6);
        return `C${point(c1)} ${point(c2)} ${point(end)}`;
    });
    const route = (from, to) => `M${point(points[from])}${segments.slice(from, to).join('')}`;
    path.setAttribute('d', route(0, points.length - 1));
    const length = path.getTotalLength();
    const split = 13;
    const measure = document.createElementNS(ns, 'path');
    measure.setAttribute('d', route(0, 1));
    const entrance = measure.getTotalLength();
    measure.setAttribute('d', route(1, split));
    const backLength = measure.getTotalLength();
    measure.setAttribute('d', route(split - 1, split));
    const frontLead = measure.getTotalLength();
    measure.setAttribute('d', route(points.length - 2, points.length - 1));
    const tailOffset = measure.getTotalLength();

    // Separate masks prevent the first pass through the crossing from revealing
    // the foreground ribbon before the rocket has flown around the loop.
    const defs = document.createElementNS(ns, 'defs');
    const masks = Array.from(surfaces, (surface) => {
        const back = surface.dataset.trailSurface === 'back';
        const mask = document.createElementNS(ns, 'mask');
        const reveal = document.createElementNS(ns, 'path');
        mask.id = `home-rocket-reveal-${surface.dataset.trailSurface}`;
        mask.setAttribute('maskUnits', 'userSpaceOnUse');
        mask.setAttribute('x', '-200');
        mask.setAttribute('y', '-250');
        mask.setAttribute('width', '2200');
        mask.setAttribute('height', '1700');
        reveal.setAttribute('d', back ? route(0, split) : route(split - 1, points.length - 1));
        reveal.setAttribute('fill', 'none');
        reveal.setAttribute('stroke', 'white');
        reveal.setAttribute('stroke-width', '168');
        const surfaceLength = reveal.getTotalLength();
        reveal.setAttribute('stroke-dasharray', `${surfaceLength} ${surfaceLength}`);
        mask.append(reveal);
        defs.append(mask);
        surface.setAttribute('mask', `url(#${mask.id})`);
        return {
            surface,
            reveal,
            length: surfaceLength,
            start: back ? -entrance : backLength - frontLead
        };
    });
    trail.prepend(defs);
    artwork.dataset.rocketInitialized = 'true';

    const restTransform = ship.getAttribute('transform');
    const state = { distance: 0 };
    const position = (distance) => {
        const value = path.getPointAtLength(Math.max(0, Math.min(length, distance)));
        return { x: 218 + value.x * (582 / 1533), y: 299 + value.y * (410 / 1036) };
    };

    function render() {
        const location = position(state.distance);
        const before = position(state.distance - 2);
        const after = position(state.distance + 2);
        const angle = (Math.atan2(after.y - before.y, after.x - before.x) * 180) / Math.PI + 90;
        ship.setAttribute('transform', `translate(${location.x} ${location.y}) rotate(${angle})`);
        masks.forEach((mask) => {
            const revealed = Math.max(
                0,
                Math.min(mask.length, state.distance - entrance - tailOffset - mask.start)
            );
            mask.reveal.setAttribute('stroke-dashoffset', mask.length - revealed);
        });
    }

    function finish() {
        timeline.pause();
        observer.disconnect();
        masks.forEach((mask) => mask.surface.removeAttribute('mask'));
        defs.remove();
        ship.setAttribute('transform', restTransform);
        artwork.dataset.rocketState = 'complete';
        reducedMotion.removeEventListener('change', onEnvironmentChange);
        desktop.removeEventListener('change', onEnvironmentChange);
        document.removeEventListener('visibilitychange', onEnvironmentChange);
    }

    function onEnvironmentChange() {
        if (reducedMotion.matches || !desktop.matches || document.hidden) finish();
    }

    const timeline = createTimeline({ autoplay: false, onRender: render, onComplete: finish });
    timeline.add(state, { distance: [0, length], duration: 2600, ease: 'in(2)' }, 0);
    const observer = new IntersectionObserver(
        (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                observer.disconnect();
                artwork.dataset.rocketState = 'flying';
                timeline.play();
            }
        },
        { rootMargin: '0px 0px -25% 0px' }
    );

    reducedMotion.addEventListener('change', onEnvironmentChange);
    desktop.addEventListener('change', onEnvironmentChange);
    document.addEventListener('visibilitychange', onEnvironmentChange);
    artwork.dataset.rocketState = 'waiting';
    render();
    observer.observe(panel);
    return timeline;
}
